import React, { useEffect, useState } from "react";
import {
  Button,
  PageHeader,
  AdminLayout,
  useToast,
  LoadingSpinner,
  ErrorDisplay,
  EmptyState,
  PaginationControls,
} from "../../components/AdminSharedComponents";
import { FilterContainer } from "../../components/ui/FilterContainer";
import { SearchInput } from "../../components/ui/SearchInput";
import { DropdownFilter } from "../../components/ui/DropdownFilter";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Map,
  Plus,
  Lock,
  Building,
  FolderXIcon,
  FolderIcon,
} from "lucide-react";
import ReportModal from "../../report/ReportModal";
import ProjectList from "./ProjectList";
import ProjectStats from "./ProjectStats";
import ProjectDeleteModal from "./ProjectDeleteModal";
import { fetchProjects } from "./utils";

import api from "../../../../../utils/api";

const Projects = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [projectReports, setProjectReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [userRole, setUserRole] = useState("");
  const [showStats /* setShowStats */] = useState(true);
  const itemsPerPage = 6;

  const isSupervisor = userRole === "supervisor";
  const hasWriteAccess = !isSupervisor;

  const fetchUserRole = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const role = user?.role || "admin";
      setUserRole(role);
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole("admin");
      toast.error("Failed to fetch user permissions");
    }
  };

  const openReportModal = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (project) => {
    if (isSupervisor) {
      toast.error("You don't have permission to delete projects");
      return;
    }
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async (id, deleteReports) => {
    try {
      await api.delete(`/api/projects/${id}?deleteReports=${deleteReports}`);
      fetchProjects(
        setProjects,
        setProjectReports,
        setError,
        setLoading,
        toast
      );
      setIsDeleteModalOpen(false);
      toast.success(
        `Project "${projectToDelete.name}" has been deleted successfully`
      );
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project. Please try again.");
    }
  };

  const handleAddProject = () => {
    if (isSupervisor) {
      toast.error("You don't have permission to create projects");
      return;
    }
    navigate("/admin/projects/add");
  };

  useEffect(() => {
    fetchUserRole();
    fetchProjects(setProjects, setProjectReports, setError, setLoading, toast);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = project.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilter = filter === "all" || project.status === filter;
    return matchesSearch && matchesFilter;
  });
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-64 sm:min-h-96 px-4">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-4 text-sm sm:text-base text-gray-600">
              Loading projects...
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8">
          <ErrorDisplay error={error} />
        </div>
      </AdminLayout>
    );
  }

  if (projects.length === 0) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8">
          <PageHeader
            title="Projects"
            subtitle={
              isSupervisor
                ? "View all projects"
                : "Manage and view all projects"
            }
          />
          <div className="flex items-center justify-center min-h-64 sm:min-h-96">
            <EmptyState
              icon={Building}
              message="No projects available yet. Create your first project to get started."
              action={
                hasWriteAccess
                  ? { label: "Add First Project", onClick: handleAddProject }
                  : null
              }
            />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader
        title={
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2 sm:gap-0">
            <span className="text-xl sm:text-2xl lg:text-3xl">Projects</span>
            {isSupervisor && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-blue-700 border border-blue-200 self-start sm:self-auto">
                <Lock className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="hidden xs:inline">Supervisor View</span>
                <span className="xs:hidden">Supervisor</span>
              </span>
            )}
          </div>
        }
        subtitle={
          isSupervisor ? "View all projects" : "Manage and view all projects"
        }
        actions={
          !isSupervisor && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Mobile: Stack buttons vertically */}
              <div className="flex flex-col sm:hidden gap-2">
                <Button
                  variant="secondary"
                  className="flex items-center justify-center gap-2 w-full text-sm"
                  onClick={() => navigate("/admin/reports")}
                >
                  <FileText className="h-4 w-4" />
                  <span>View Reports</span>
                </Button>

                <Button
                  variant="secondary"
                  className="flex items-center justify-center gap-2 w-full text-sm"
                  onClick={() => navigate("/admin/projects/map")}
                >
                  <Map className="h-4 w-4" />
                  <span>Map View</span>
                </Button>

                <Button
                  variant="primary"
                  className="flex items-center justify-center gap-2 w-full text-sm"
                  onClick={handleAddProject}
                >
                  <Plus className="h-4 w-4" />
                  <span>Add New Project</span>
                </Button>
              </div>

              {/* Desktop: Horizontal layout */}
              <div className="hidden sm:flex gap-3">
                <Button
                  variant="secondary"
                  className="flex items-center gap-1"
                  onClick={() => navigate("/admin/reports")}
                >
                  <FileText className="h-4 w-4" /> <span>View Reports</span>
                </Button>

                <Button
                  variant="secondary"
                  className="flex items-center gap-1"
                  onClick={() => navigate("/admin/projects/map")}
                >
                  <Map className="h-4 w-4" /> <span>Map View</span>
                </Button>

                <Button
                  variant="primary"
                  className="flex items-center gap-1"
                  onClick={handleAddProject}
                >
                  <Plus className="h-4 w-4" /> <span>Add New Project</span>
                </Button>
              </div>
            </div>
          )
        }
      />

      {showStats && (
        <div className="mb-4 sm:mb-6">
          <ProjectStats projects={projects} projectReports={projectReports} />
        </div>
      )}

      <div className="mb-4 sm:mb-6">
        <FilterContainer>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
            <div className="w-full sm:flex-1">
              <SearchInput value={searchTerm} onChange={setSearchTerm} />
            </div>
            <div className="w-full sm:w-auto sm:min-w-48">
              <DropdownFilter
                label="Status"
                value={filter}
                onChange={setFilter}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "Completed", label: "Completed" },
                  { value: "In Progress", label: "In Progress" },
                  { value: "On Hold", label: "On Hold" },
                  { value: "Cancelled", label: "Cancelled" },
                ]}
              />
            </div>
          </div>
        </FilterContainer>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="flex items-center justify-center min-h-48 sm:min-h-64">
          <EmptyState
            icon={searchTerm || filter !== "all" ? FolderXIcon : FolderIcon}
            message={
              searchTerm || filter !== "all"
                ? "No projects found matching your search criteria. Try adjusting your search term or filter."
                : "No projects available."
            }
            action={
              searchTerm || filter !== "all"
                ? {
                    label: "Clear Filters",
                    onClick: () => {
                      setSearchTerm("");
                      setFilter("all");
                      setCurrentPage(1);
                    },
                  }
                : hasWriteAccess
                ? { label: "Add New Project", onClick: handleAddProject }
                : null
            }
          />
        </div>
      ) : (
        <>
          <ProjectList
            projects={paginatedProjects}
            projectReports={projectReports}
            onDeleteClick={handleDeleteClick}
            onOpenReportModal={openReportModal}
            userRole={userRole}
            filteredCount={filteredProjects.length}
            paginatedCount={paginatedProjects.length}
            searchTerm={searchTerm}
            filter={filter}
          />

          {Math.ceil(filteredProjects.length / itemsPerPage) > 1 && (
            <div className="mt-6 sm:mt-8 flex justify-center px-2">
              <PaginationControls
                currentPage={currentPage}
                totalPages={Math.ceil(filteredProjects.length / itemsPerPage)}
                onNext={() =>
                  setCurrentPage((prev) =>
                    Math.min(
                      prev + 1,
                      Math.ceil(filteredProjects.length / itemsPerPage)
                    )
                  )
                }
                onPrev={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </>
      )}

      {isModalOpen && selectedReport && (
        <ReportModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          report={selectedReport}
          project={projects.find((p) => p.id === selectedReport.project_id)}
        />
      )}

      <ProjectDeleteModal
        isOpen={isDeleteModalOpen}
        project={projectToDelete}
        reports={projectReports[projectToDelete?.id]}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDelete}
      />
    </AdminLayout>
  );
};

export default Projects;
